<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TraceTimeline from 'dashboard/routes/dashboard/settings/aiAgents/studio/components/TraceTimeline.vue';
import IntentFeedbackForm from 'dashboard/routes/dashboard/settings/aiAgents/studio/components/IntentFeedbackForm.vue';
import AiBotTracesAPI from 'dashboard/api/aiBot/traces';
import AiBotIntentSetsAPI from 'dashboard/api/aiBot/intentSets';
import { barWidthClass } from 'dashboard/routes/dashboard/settings/aiAgents/studio/components/barWidth';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const panelRef = ref(null);
const trace = ref(null);
const isLoading = ref(false);
const routeOptions = ref([]);

// The aegis bot answering this conversation is its AI assignee; the drawer
// only opens for bot conversations, so meta.assignee is the bot (with id).
const currentChat = useMapGetter('getSelectedChat');
const botId = computed(() => {
  const meta = currentChat.value?.meta;
  return meta?.assignee_type === 'AgentBot'
    ? (meta.assignee?.id ?? null)
    : null;
});

const decision = computed(() => trace.value?.intent_decision || null);

const percentLabel = value => `${Math.round((value || 0) * 100)}%`;

const routeBadge = computed(() =>
  decision.value
    ? `${decision.value.route} ${percentLabel(decision.value.confidence)}`
    : ''
);

const messageExcerpt = computed(() => {
  const text = props.message.content || decision.value?.normalized_text || '';
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
});

const probabilityRows = computed(() =>
  decision.value
    ? Object.entries(decision.value.probabilities || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
    : []
);

const fetchRoutes = async bot => {
  const response = await new AiBotIntentSetsAPI(bot).get();
  const active =
    response.data.find(set => set.name === 'custom') || response.data[0];
  routeOptions.value = Object.keys(active?.routes || {}).map(route => ({
    value: route,
    label: route,
  }));
};

const fetchTrace = async () => {
  if (!botId.value) return;
  isLoading.value = true;
  trace.value = null;
  try {
    const api = new AiBotTracesAPI(botId.value);
    const traceId = props.message.content_attributes?.ai_bot?.trace_id;
    if (traceId) {
      const { data } = await api.show(traceId);
      trace.value = data;
    } else {
      // Inbound contact message: the trace answers it by message_id, so find
      // the trace of this conversation whose message_id matches.
      const { data } = await api.get({
        conversation_id: props.message.conversation_id,
      });
      const match = (data.payload || []).find(
        row => row.message_id === props.message.id
      );
      if (match) {
        const detail = await api.show(match.id);
        trace.value = detail.data;
      }
    }
    if (trace.value?.agent_bot_id) await fetchRoutes(trace.value.agent_bot_id);
  } finally {
    isLoading.value = false;
  }
};

const open = async () => {
  panelRef.value?.open();
  await fetchTrace();
};

const close = () => {
  panelRef.value?.close();
  emit('close');
};

const onFeedbackSaved = saved => {
  if (trace.value) {
    trace.value = { ...trace.value, intent_decision: saved };
  }
};

defineExpose({ open, close, trace, botId });
</script>

<template>
  <SidePanel
    ref="panelRef"
    :title="t('AI_AGENT.TRACE.DRAWER_TITLE')"
    :description="messageExcerpt"
    width="2xl"
    @close="emit('close')"
  >
    <template #default>
      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner class="size-6" />
      </div>
      <div v-else-if="!trace" class="flex flex-col gap-2 py-6">
        <p v-if="!botId" class="text-body-main text-n-slate-11">
          {{ t('AI_AGENT.TRACE.NO_BOT') }}
        </p>
        <p v-else class="text-body-main text-n-slate-11">
          {{ t('AI_AGENT.TRACE.EMPTY') }}
        </p>
      </div>
      <div v-else class="flex flex-col gap-6">
        <TraceTimeline :trace="trace" />
        <div
          v-if="decision"
          class="flex flex-col gap-3 p-4 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
          data-testid="drawer-intent-decision"
        >
          <div class="flex items-center justify-between gap-2">
            <h4 class="flex items-center gap-2 text-heading-3 text-n-slate-12">
              <span class="i-lucide-brain size-4 text-n-slate-10" />
              {{ t('AI_AGENT.TRACE.INTENT_SECTION') }}
            </h4>
            <span
              v-if="decision.route"
              class="px-2 py-0.5 font-mono rounded-full bg-n-brand/10 text-label-small text-n-blue-11"
            >
              {{ routeBadge }}
            </span>
            <span
              v-else
              class="px-2 py-0.5 rounded-full bg-n-ruby-9/10 text-label-small text-n-ruby-11"
            >
              {{
                t('AI_AGENT.INTENT.TABLE.LOST_BADGE', {
                  reason: decision.reason,
                })
              }}
            </span>
          </div>
          <div
            v-for="([label, probability], index) in probabilityRows"
            :key="label"
            class="grid items-center grid-cols-[10rem_1fr_3rem] gap-3"
          >
            <span
              class="font-mono truncate text-label-small"
              :class="index === 0 ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{ label }}
            </span>
            <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-1.5 rounded-full"
                :class="[
                  barWidthClass(probability, 1),
                  index === 0 ? 'bg-n-brand' : 'bg-n-slate-8',
                ]"
              />
            </div>
            <span
              class="text-right tabular-nums text-label-small text-n-slate-11"
            >
              {{ percentLabel(probability) }}
            </span>
          </div>
          <p
            v-if="!probabilityRows.length"
            class="text-body-main text-n-slate-11"
          >
            {{ decision.normalized_text }}
          </p>
          <IntentFeedbackForm
            v-if="trace.agent_bot_id"
            :bot-id="trace.agent_bot_id"
            :decision="decision"
            :route-options="routeOptions"
            @saved="onFeedbackSaved"
          />
        </div>
      </div>
    </template>
  </SidePanel>
</template>
